import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { completionsAPI, dailyQuestionsAPI } from '../services/api';
import colors, { spacing, shadows, borderRadius } from '../theme';
import FlashcardCarousel from '../components/FlashcardCarousel';

function Dashboard() {
    const [assignments, setAssignments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState(null);
    const [dailyQuestion, setDailyQuestion] = useState(null);
    const [dailyStreak, setDailyStreak] = useState(null);
    const [dailyLeaderboard, setDailyLeaderboard] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submittingAnswer, setSubmittingAnswer] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    useEffect(() => {
        // Get user from localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }

        // Check for payment status in URL
        const payment = searchParams.get('payment');
        if (payment === 'success') {
            setPaymentMessage({ type: 'success', text: '🎉 Payment successful! Your subscription is now active.' });
            // Clear the query param
            searchParams.delete('payment');
            setSearchParams(searchParams);
        } else if (payment === 'canceled') {
            setPaymentMessage({ type: 'error', text: 'Payment was canceled. You can try again anytime.' });
            // Clear the query param
            searchParams.delete('payment');
            setSearchParams(searchParams);
        }

        // Fetch data
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]); // Reload data when navigating back to dashboard

    const loadData = async () => {
        try {
            const results = await Promise.allSettled([
                completionsAPI.getAssignments(),
                completionsAPI.getSummary(),
                dailyQuestionsAPI.getToday(),
                dailyQuestionsAPI.getStreak(),
                dailyQuestionsAPI.getLeaderboard(5),
                completionsAPI.getFlashcards()
            ]);

            // Safely extract data from settled promises
            if (results[0].status === 'fulfilled') {
                setAssignments(results[0].value.data.data);
            } else {
                console.error('Failed to load assignments:', results[0].reason);
            }

            if (results[1].status === 'fulfilled') {
                setSummary(results[1].value.data.data);
            } else {
                console.error('Failed to load summary:', results[1].reason);
            }

            if (results[2].status === 'fulfilled') {
                setDailyQuestion(results[2].value.data.data);
            } else {
                console.error('Failed to load daily question:', results[2].reason);
            }

            if (results[3].status === 'fulfilled') {
                setDailyStreak(results[3].value.data.data);
            } else {
                console.error('Failed to load streak:', results[3].reason);
            }

            if (results[4].status === 'fulfilled') {
                setDailyLeaderboard(results[4].value.data.data);
            } else {
                console.error('Failed to load leaderboard:', results[4].reason);
            }

            if (results[5].status === 'fulfilled') {
                setFlashcards(results[5].value.data.data);
            } else {
                console.error('Failed to load flashcards:', results[5].reason);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error loading dashboard:', error);
            setLoading(false);
        }
    };

    const handleAnswerSubmit = async () => {
        if (selectedAnswer === null) return;

        setSubmittingAnswer(true);
        try {
            await dailyQuestionsAPI.submitAnswer({
                dailyQuestionId: dailyQuestion.dailyQuestionId,
                answerIndex: selectedAnswer
            });

            // Refresh question and streak data
            const results = await Promise.allSettled([
                dailyQuestionsAPI.getToday(),
                dailyQuestionsAPI.getStreak(),
                dailyQuestionsAPI.getLeaderboard(5)
            ]);

            if (results[0].status === 'fulfilled') {
                setDailyQuestion(results[0].value.data.data);
            }
            if (results[1].status === 'fulfilled') {
                setDailyStreak(results[1].value.data.data);
            }
            if (results[2].status === 'fulfilled') {
                setDailyLeaderboard(results[2].value.data.data);
            }

            setSelectedAnswer(null);
        } catch (error) {
            console.error('Error submitting answer:', error);
            alert('Failed to submit answer');
        } finally {
            setSubmittingAnswer(false);
        }
    };

    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${colors.gray[50]} 0%, ${colors.blue[50]} 100%)`
        }}>
            {/* Header */}


            {/* Main Content */}
            <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
                {/* Payment Success/Error Message */}
                {paymentMessage && (
                    <div style={{
                        padding: '1rem 1.5rem',
                        marginBottom: '2rem',
                        borderRadius: borderRadius.lg,
                        backgroundColor: paymentMessage.type === 'success' ? colors.green[50] : colors.red[50],
                        border: `2px solid ${paymentMessage.type === 'success' ? colors.green[200] : colors.red[200]}`,
                        color: paymentMessage.type === 'success' ? colors.green[800] : colors.red[800],
                        fontSize: '1rem',
                        fontWeight: '500',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <span>{paymentMessage.text}</span>
                        <button
                            onClick={() => setPaymentMessage(null)}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                color: paymentMessage.type === 'success' ? colors.green[600] : colors.red[600],
                                padding: '0 0.5rem',
                            }}
                        >
                            ×
                        </button>
                    </div>
                )}

                <h2 style={{ marginBottom: '1rem' }}>👋 Welcome back, {user?.name}!</h2>

                {/* Top Row: Leaderboard, Question of the Day, Flashcards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: spacing.lg,
                    marginBottom: spacing.xl
                }}>
                    {/* Leaderboard */}
                    {dailyLeaderboard.length > 0 && (
                        <div style={{
                            backgroundColor: colors.white,
                            padding: spacing.md,
                            borderRadius: borderRadius.lg,
                            boxShadow: shadows.md,
                            border: `1px solid ${colors.gray[100]}`
                        }}>
                            <h4 style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: colors.primary,
                                marginBottom: spacing.sm
                            }}>
                                🏆 Leaderboard
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                                {dailyLeaderboard.map((leaderboardUser, idx) => {
                                    const isCurrentUser = leaderboardUser.userId === user?.id;
                                    const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
                                    const initials = leaderboardUser.userName?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: spacing.xs,
                                                borderRadius: borderRadius.sm,
                                                backgroundColor: isCurrentUser
                                                    ? colors.blue[100]
                                                    : idx === 0 ? colors.yellow[50] : colors.gray[50],
                                                border: isCurrentUser ? `2px solid ${colors.blue[500]}` : 'none',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                                                {/* Medal or Rank */}
                                                {medal ? (
                                                    <span style={{ fontSize: '1.25rem', minWidth: '24px' }}>
                                                        {medal}
                                                    </span>
                                                ) : (
                                                    <span style={{ fontWeight: '600', color: colors.gray[500], minWidth: '24px', fontSize: '0.75rem' }}>
                                                        #{idx + 1}
                                                    </span>
                                                )}

                                                {/* Avatar */}
                                                <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isCurrentUser ? colors.blue[500] : colors.primary,
                                                    color: colors.white,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {initials}
                                                </div>

                                                {/* Name */}
                                                <span style={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: isCurrentUser ? '600' : '400',
                                                    color: isCurrentUser ? colors.blue[900] : colors.gray[900]
                                                }}>
                                                    {leaderboardUser.userName}{isCurrentUser && ' (You)'}
                                                </span>
                                            </div>

                                            {/* Streak */}
                                            <span style={{
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                color: colors.primary
                                            }}>
                                                🔥 {leaderboardUser.streak}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Question of the Day - Compact */}
                    {dailyQuestion ? (
                        <div style={{
                            backgroundColor: colors.white,
                            padding: spacing.md,
                            borderRadius: borderRadius.lg,
                            boxShadow: shadows.md,
                            border: `1px solid ${colors.gray[100]}`
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm }}>
                                <h4 style={{
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: colors.primary,
                                    margin: 0
                                }}>
                                    ❓ Question of the Day
                                </h4>
                                {dailyStreak && (
                                    <span style={{
                                        fontSize: '0.875rem',
                                        padding: '4px 12px',
                                        backgroundColor: colors.orange[100],
                                        color: colors.orange[700],
                                        borderRadius: borderRadius.md,
                                        fontWeight: '700',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        🔥 {dailyStreak.currentStreak}
                                    </span>
                                )}
                            </div>

                            {dailyQuestion.hasAnswered ? (
                                <div>
                                    <p style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        color: colors.gray[800],
                                        marginBottom: spacing.sm
                                    }}>
                                        {dailyQuestion.question}
                                    </p>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                                        {dailyQuestion.answers.map((answer, idx) => {
                                            const isUserAnswer = idx === dailyQuestion.userAnswer.answerIndex;
                                            const isCorrect = idx === dailyQuestion.userAnswer.correctAnswer;

                                            let backgroundColor = colors.white;
                                            let borderColor = colors.gray[200];

                                            if (isCorrect) {
                                                backgroundColor = colors.green[50];
                                                borderColor = colors.green[500];
                                            } else if (isUserAnswer) {
                                                backgroundColor = colors.red[50];
                                                borderColor = colors.red[500];
                                            }

                                            return (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        padding: '6px 10px',
                                                        borderRadius: borderRadius.sm,
                                                        border: `2px solid ${borderColor}`,
                                                        backgroundColor,
                                                        fontSize: '0.75rem',
                                                        fontWeight: isCorrect ? '600' : '400'
                                                    }}
                                                >
                                                    {isCorrect && '✓ '}{isUserAnswer && !isCorrect && '✗ '}{answer}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <p style={{
                                        marginTop: spacing.sm,
                                        fontSize: '0.75rem',
                                        color: dailyQuestion.userAnswer.isCorrect ? colors.green[700] : colors.red[700],
                                        fontWeight: '600'
                                    }}>
                                        {dailyQuestion.userAnswer.isCorrect ? '🎉 Correct!' : '❌ Incorrect'}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <p style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        color: colors.gray[800],
                                        marginBottom: spacing.sm
                                    }}>
                                        {dailyQuestion.question}
                                    </p>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, marginBottom: spacing.sm }}>
                                        {dailyQuestion.answers.map((answer, idx) => (
                                            <label
                                                key={idx}
                                                style={{
                                                    padding: '6px 10px',
                                                    borderRadius: borderRadius.sm,
                                                    border: `2px solid ${selectedAnswer === idx ? colors.primary : colors.gray[200]}`,
                                                    backgroundColor: selectedAnswer === idx ? colors.blue[50] : colors.white,
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="daily-question"
                                                    checked={selectedAnswer === idx}
                                                    onChange={() => setSelectedAnswer(idx)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <span>{answer}</span>
                                            </label>
                                        ))}
                                    </div>

                                    <button
                                        onClick={handleAnswerSubmit}
                                        disabled={selectedAnswer === null || submittingAnswer}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: selectedAnswer === null ? colors.gray[300] : colors.primary,
                                            color: colors.white,
                                            border: 'none',
                                            borderRadius: borderRadius.sm,
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            cursor: selectedAnswer === null ? 'not-allowed' : 'pointer',
                                            width: '100%'
                                        }}
                                    >
                                        {submittingAnswer ? 'Submitting...' : 'Submit Answer'}
                                    </button>
                                </div>
                            )}

                            <p style={{
                                marginTop: spacing.xs,
                                fontSize: '0.625rem',
                                color: colors.gray[500]
                            }}>
                                From: {dailyQuestion.knowledgeTitle}
                            </p>
                        </div>
                    ) : user?.role === 'admin' && (
                        <div style={{
                            backgroundColor: colors.white,
                            padding: spacing.md,
                            borderRadius: borderRadius.lg,
                            boxShadow: shadows.md,
                            border: `1px solid ${colors.gray[100]}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '160px'
                        }}>
                            <h4 style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: colors.primary,
                                marginBottom: spacing.sm
                            }}>
                                ❓ Question of the Day
                            </h4>
                            <p style={{
                                fontSize: '0.75rem',
                                color: colors.gray[600],
                                marginBottom: spacing.md,
                                textAlign: 'center'
                            }}>
                                No question available yet for today.
                            </p>
                            <button
                                onClick={async () => {
                                    try {
                                        const result = await dailyQuestionsAPI.generate();
                                        if (result.data.success) {
                                            // Reload the daily question
                                            const questionResponse = await dailyQuestionsAPI.getToday();
                                            setDailyQuestion(questionResponse.data.data);
                                        }
                                    } catch (error) {
                                        alert(error.response?.data?.error || 'Failed to generate question');
                                    }
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: colors.primary,
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: borderRadius.sm,
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Generate Question Now
                            </button>
                        </div>
                    )}

                    {/* Flashcards - Compact */}
                    {flashcards.length > 0 && (
                        <div style={{
                            backgroundColor: colors.white,
                            padding: spacing.md,
                            borderRadius: borderRadius.lg,
                            boxShadow: shadows.md,
                            border: `1px solid ${colors.gray[100]}`
                        }}>
                            <FlashcardCarousel flashcards={flashcards} />
                        </div>
                    )}
                </div>

                {/* Bottom Row: Pending Assignments + Quiz Stats Sidebar */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 320px',
                    gap: spacing.lg,
                    marginBottom: spacing.xl
                }}>
                    {/* Pending Assignments - Left Column */}
                    <div>
                        <h3 style={{
                            marginBottom: spacing.md,
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            color: colors.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📋 Pending Assignments
                        </h3>

                        {assignments.length === 0 ? (
                            <p style={{ color: colors.gray[600] }}>No pending assignments. Great job!</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                                {assignments.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => navigate(`/knowledge/${item.id}`)}
                                        style={{
                                            backgroundColor: colors.white,
                                            padding: spacing.lg,
                                            borderRadius: borderRadius.lg,
                                            boxShadow: shadows.md,
                                            borderLeft: `4px solid ${colors.accent1}`,
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.boxShadow = shadows.lg;
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.boxShadow = shadows.md;
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <h4 style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: '600', color: colors.primary }}>
                                            {item.title}
                                        </h4>
                                        <p style={{ color: colors.gray[600], fontSize: '0.875rem', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                                            {item.description}
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <span style={{
                                                fontSize: '0.75rem',
                                                padding: '4px 12px',
                                                backgroundColor: colors.accent3,
                                                color: colors.dark,
                                                borderRadius: borderRadius.xl,
                                                fontWeight: '500'
                                            }}>
                                                {item.category}
                                            </span>
                                            <span style={{
                                                fontSize: '0.75rem',
                                                padding: '4px 12px',
                                                backgroundColor: item.assessment_type === 'quiz' ? colors.accent1 : colors.gray[200],
                                                color: colors.white,
                                                borderRadius: borderRadius.xl,
                                                fontWeight: '500'
                                            }}>
                                                {item.assessment_type === 'quiz' ? '📝 Quiz' : '✓ Acknowledgment'}
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: colors.gray[600], padding: '4px 0', fontWeight: '500' }}>
                                                by {item.creator_name}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quiz Stats Sidebar - Right Column */}
                    <div>
                        <h3 style={{
                            marginBottom: spacing.md,
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: colors.primary
                        }}>
                            📊 Quiz Stats
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                            {/* Completed Card */}
                            <div style={{
                                backgroundColor: colors.white,
                                padding: spacing.lg,
                                borderRadius: borderRadius.lg,
                                boxShadow: shadows.md,
                                border: `1px solid ${colors.gray[100]}`
                            }}>
                                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: colors.primary, marginBottom: '4px' }}>
                                    {summary?.totalCompleted ?? 0}
                                </div>
                                <div style={{ color: colors.gray[600], fontSize: '0.875rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Completed (100%)
                                </div>
                            </div>

                            {/* Quiz Accuracy Card */}
                            <div style={{
                                backgroundColor: colors.white,
                                padding: spacing.lg,
                                borderRadius: borderRadius.lg,
                                boxShadow: shadows.md,
                                border: `1px solid ${colors.gray[100]}`
                            }}>
                                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: colors.primary, marginBottom: '4px' }}>
                                    {summary?.quizStatistics?.averageScore !== undefined
                                        ? `${Math.round(summary.quizStatistics.averageScore)}%`
                                        : '—'}
                                </div>
                                <div style={{ color: colors.gray[600], fontSize: '0.875rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Quiz Accuracy
                                </div>
                            </div>

                            {/* Avg Tries Card */}
                            <div style={{
                                backgroundColor: colors.white,
                                padding: spacing.lg,
                                borderRadius: borderRadius.lg,
                                boxShadow: shadows.md,
                                border: `1px solid ${colors.gray[100]}`
                            }}>
                                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: colors.primary, marginBottom: '4px' }}>
                                    {summary?.quizStatistics?.averageAttemptsTo100
                                        ? summary.quizStatistics.averageAttemptsTo100
                                        : 'N/A'}
                                </div>
                                <div style={{ color: colors.gray[600], fontSize: '0.875rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Avg Tries to 100%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;